import {
  Box,
  Flex,
  Heading,
  IconButton,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react'
import { LandingContainer } from 'components/common/LandingContainer'
import OmensLogoType from 'components/svg/OmensLogoType'
import { Body2, H1, H2, H3, H4, H5, H6 } from 'components/typography'
import { MEDIA_LINKS_MEDLEVE } from 'constants/mediaLinks'
import Image from 'next/image'
import Link from 'next/link'
import { FC, ReactNode } from 'react'
import { FaPlay } from 'react-icons/fa'

import { medleveProfileUrl } from '../../modules/cuidei/landings/seo'

// Medleve is the default; Cuidei reuses the block inside its own landing, in its own colors.
const PALETTES = {
  medleve: {
    bg: 'alabaster',
    text: undefined,
    heading: 'phthalo',
    accent: '#5f8ed1',
    divider: '#0B2730',
    modalBg: 'alabaster',
    layout: { py: { base: 16, lg: 40 }, gap: { base: 14, lg: 28 } }
  },
  cuidei: {
    bg: 'cuidei.10',
    text: 'cuidei.ink',
    heading: 'cuidei.ink',
    // Raw hex (cuidei.60 / cuidei.20): they go inside border shorthands.
    accent: '#2F6BED',
    divider: '#DCE8FF',
    modalBg: 'white',
    // Sits between other landing sections on the same background: their spacing and gutters apply.
    layout: { py: 0, px: 0, gap: { base: 10, lg: 16 } }
  }
} as const

const RECLAME_AQUI_URL = 'https://www.reclameaqui.com.br/empresa/medleve/'

// Cuidei press grid: every logo fits the same slot.
const LOGO_SLOT = { base: 92, lg: 124 }
// Same logo area (not the same height) gives a wide wordmark and a square mark the same optical size;
// the height cap keeps solid square marks (g1, GQ) from outweighing the wordmarks.
const LOGO_AREA = { base: 1100, lg: 1900 }
const LOGO_MAX_H = { base: 20, lg: 26 }

const logoBox = (aspect: number, bp: 'base' | 'lg') => {
  let h = Math.min(Math.sqrt(LOGO_AREA[bp] / aspect), LOGO_MAX_H[bp])
  if (h * aspect > LOGO_SLOT[bp]) h = LOGO_SLOT[bp] / aspect
  return { w: `${Math.round(h * aspect)}px`, h: `${Math.round(h)}px` }
}

// 3 logos per row on mobile (4 would shrink the wordmarks), 4 on desktop; a short last row stays centered.
const PRESS_COLUMNS = { base: 3, lg: 4 }
const PRESS_GAP = { base: 16, lg: 32 }
const pressCellBasis = (bp: 'base' | 'lg') =>
  `calc((100% - ${(PRESS_COLUMNS[bp] - 1) * PRESS_GAP[bp]}px) / ${PRESS_COLUMNS[bp]})`

// The links look like static logos: no underline, no link color, only a slight fade on hover (pointer devices,
// so a tap doesn't stick) and a focus ring for keyboard users.
const pressLinkProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
  display: 'inline-flex',
  alignItems: 'center',
  color: 'inherit',
  borderRadius: '4px',
  transition: 'opacity 0.15s',
  _hover: { textDecoration: 'none' },
  _focusVisible: { outline: '2px solid', outlineColor: 'cuidei.60', outlineOffset: '4px' },
  sx: { '@media (hover: hover)': { '&:hover': { opacity: 0.8 } } }
} as const

const CuideiPressList: FC<{ divider: string; logoFill: string }> = ({ divider, logoFill }) => (
  <Flex
    as="ul"
    listStyleType="none"
    borderTop={`1px solid ${divider}`}
    pt={{ base: 6, lg: 8 }}
    wrap="wrap"
    justify="center"
    align="center"
    columnGap={{ base: `${PRESS_GAP.base}px`, lg: `${PRESS_GAP.lg}px` }}
    rowGap={{ base: 6, lg: 8 }}
  >
    {/* Each logo opens its article; the link names the outlet and the headline for screen readers. */}
    {MEDIA_LINKS_MEDLEVE.map(({ icon, href, outlet, title, aspect }) => {
      const mobile = logoBox(aspect, 'base')
      const desktop = logoBox(aspect, 'lg')
      return (
        <Flex
          as="li"
          key={href}
          flexBasis={{ base: pressCellBasis('base'), lg: pressCellBasis('lg') }}
          justify="center"
          align="center"
          minH={{ base: '48px', lg: '64px' }}
        >
          <ChakraLink href={href} aria-label={`${outlet}: ${title}`} {...pressLinkProps}>
            <Box
              as={icon}
              aria-label={outlet}
              role="img"
              fill={logoFill}
              w={{ base: mobile.w, lg: desktop.w }}
              h={{ base: mobile.h, lg: desktop.h }}
            />
          </ChakraLink>
        </Flex>
      )
    })}
    <Flex
      as="li"
      flexBasis={{ base: pressCellBasis('base'), lg: pressCellBasis('lg') }}
      justify="center"
      align="center"
      minH={{ base: '48px', lg: '64px' }}
    >
      <ChakraLink href={RECLAME_AQUI_URL} w={{ base: '80px', lg: '104px' }} overflow="hidden" {...pressLinkProps}>
        <Image
          src="/landings/selo-reclame-aqui-1.png"
          alt="Selo Reclame Aqui"
          width={120}
          height={63}
          style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
        />
      </ChakraLink>
    </Flex>
  </Flex>
)

interface DoctorCardProps {
  name: ReactNode
  background: string
  crm: string
  photo: { src: string; alt: string }
  onPlay: () => void
  cuidei: boolean
}

/**
 * One doctor card: his photo with the video button, name, background and CRM. The texts are the
 * same in both brands; the Cuidei card types them with the landing's own tokens (theme heading/body
 * family, i.e. Manrope, the landing's sizes and colors) instead of the Medleve display styles, so the
 * two cards read like the rest of the Cuidei page on desktop and on phones.
 */
const DoctorCard: FC<DoctorCardProps> = ({ name, background, crm, photo, onPlay, cuidei }) => (
  <Flex
    w={'full'}
    maxW={'350px'}
    borderRadius={{ base: '24px', lg: '32px' }}
    overflow={'hidden'}
    direction={'column'}
    bg={'white'}
  >
    <Flex h={'250px'} position={'relative'}>
      <Image src={photo.src} alt={photo.alt} fill sizes="350px" style={{ objectFit: 'cover' }} />
      <IconButton
        aria-label="Play video"
        icon={<FaPlay />}
        onClick={onPlay}
        position="absolute"
        bottom="50%"
        left="50%"
        transform="translate(-50%, 50%)"
        colorScheme="whiteAlpha"
        bg="rgba(0,0,0,0.6)"
        borderRadius="full"
        boxSize="50px"
        _hover={{ bg: 'rgba(0,0,0,0.8)' }}
      />
    </Flex>
    {cuidei ? (
      <Stack flex={1} px={5} py={6} spacing={1} align="center" textAlign="center">
        <Heading as="h3" fontFamily="heading" fontSize={{ base: 'lg', lg: 'xl' }} fontWeight={700} color="cuidei.ink">
          {name}
        </Heading>
        <Text fontFamily="body" fontSize={{ base: 'sm', lg: 'md' }} color="cuidei.muted" lineHeight={1.4}>
          {background}
        </Text>
        <Text fontFamily="body" fontSize="sm" fontWeight={600} color="cuidei.60" pt={1}>
          {crm}
        </Text>
      </Stack>
    ) : (
      <Flex flex={1} px={4} py={8} direction={'column'} align={'center'} gap={2}>
        <H3
          fontWeight={800}
          fontSize={{ base: '1rem', lg: '1.5rem' }}
          letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
          lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
        >
          {name}
        </H3>
        <H4
          textAlign={'center'}
          fontWeight={400}
          fontSize={{ base: '15px', lg: '20px' }}
          letterSpacing={{ base: '-0.6px', lg: '-0.15px' }}
          lineHeight={{ base: '20px', lg: '24px' }}
        >
          {background}
        </H4>
        <Body2>{crm}</Body2>
      </Flex>
    )}
  </Flex>
)

interface MedleveAboutSectionProps {
  palette?: keyof typeof PALETTES
}

const MedleveAboutSection: FC<MedleveAboutSectionProps> = ({ palette = 'medleve' }) => {
  const colors = PALETTES[palette]
  // Cuidei (GEO PRD): descriptive anchors everywhere, never a raw URL or an unlabeled logo.
  const isCuidei = palette === 'cuidei'
  const doctorName = (name: string, profileSlug: string) =>
    isCuidei ? (
      <ChakraLink href={medleveProfileUrl(profileSlug)} isExternal>
        {name}
      </ChakraLink>
    ) : (
      name
    )
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const { isOpen: isOpenDrJoao, onOpen: onOpenDrJoao, onClose: onCloseDrJoao } = useDisclosure()
  const { isOpen: isOpenDrCarlos, onOpen: onOpenDrCarlos, onClose: onCloseDrCarlos } = useDisclosure()
  return (
    <>
      <Box bg={colors.bg} color={colors.text}>
        <LandingContainer bg={colors.bg} {...colors.layout} align={'center'}>
          <Flex w="full" direction={'column'} justify={'center'} align={'center'}>
            <H2
              flex="1"
              color={colors.heading}
              maxW="full"
              textAlign="center"
              lineHeight={{ lg: '' }}
              fontSize={{ base: '1.25rem', lg: '2.25rem' }}
              sx={{ fontFeatureSettings: "'ss07' on, 'ss01' on, 'calt' off, 'liga' off" }}
              letterSpacing={{ base: '-0.02em', lg: '-0.03em' }}
            >
              {isCuidei ? (
                <>
                  Uma empresa do{' '}
                  <ChakraLink href="https://www.omens.com.br" isExternal textDecoration="underline">
                    Grupo Omens
                  </ChakraLink>
                </>
              ) : (
                <>
                  Uma empresa do grupo{' '}
                  <Link href={'https://www.omens.com.br/'} target="_blank" aria-label="Grupo Omens">
                    <OmensLogoType
                      w={{ base: '80px', lg: '120px' }}
                      h={{ base: '18px', lg: '20px' }}
                      color={colors.heading}
                    />
                  </Link>
                </>
              )}{' '}
              que já atendeu
            </H2>
            <H2
              borderBottom={{ base: `4px solid ${colors.accent}`, lg: `8px solid ${colors.accent}` }}
              fontWeight={800}
              fontSize={{ base: '2rem', lg: '3.25rem' }}
              letterSpacing="-0.04em"
              lineHeight={{ base: '2.5rem', lg: '3.75rem' }}
            >
              {isCuidei ? '+40 mil pacientes' : '+40 mil brasileiros'}
            </H2>
          </Flex>

          <Flex direction={{ base: 'column', lg: 'row' }} gap={{ base: 4, lg: 8 }}>
            <DoctorCard
              name={doctorName('Dr. João Brunhara', 'joao-brunhara')}
              background="USP - Corpo clínico Hospital Israelita Albert Einstein"
              crm="CRM SP 161642"
              photo={{ src: '/landings/dr-joao-brunhara.webp', alt: 'Dr. João Brunhara, CRM-SP 161642' }}
              onPlay={onOpenDrJoao}
              cuidei={isCuidei}
            />
            <DoctorCard
              name={doctorName('Dr. Carlos Seraphim', 'carlos-seraphim')}
              background="USP - Metabologia Clínica Médica"
              crm="CRM SP 156663"
              photo={{ src: '/landings/dr-carlos-seraphim.webp', alt: 'Dr. Carlos Seraphim, CRM-SP 156663' }}
              onPlay={onOpenDrCarlos}
              cuidei={isCuidei}
            />
          </Flex>

          <Flex direction={'column'} gap={4} w={'full'}>
            <H5>Imprensa</H5>
            {isCuidei ? (
              <CuideiPressList divider={colors.divider} logoFill={colors.heading} />
            ) : (
              <Flex
                borderTop={`1px solid ${colors.divider}`}
                direction={{ base: 'column', lg: 'row' }}
                pt={4}
                justify={'space-between'}
                gap={4}
              >
                <Flex
                  wrap="wrap"
                  gap={{ base: '.5rem', lg: '1.25rem' }}
                  align="center"
                  justifyContent={{ base: 'space-between', lg: 'normal' }}
                >
                  {MEDIA_LINKS_MEDLEVE.map(({ icon, href, outlet }) => (
                    <Box key={href}>
                      <a href={href} target="_blank" rel="noopener noreferrer" key={href} aria-label={outlet}>
                        <Box
                          as={icon}
                          fill={colors.heading}
                          w="auto"
                          maxW={{ base: '80px', lg: '150px' }}
                          maxH={{ base: '24px', lg: '30px' }}
                        />
                      </a>
                    </Box>
                  ))}
                  {isMobile && (
                    <Stack w="80px" borderRadius={'8px'} overflow={'hidden'}>
                      <Link href={'https://www.reclameaqui.com.br/empresa/medleve/'} target="_blank">
                        <Image src="/landings/selo-reclame-aqui-1.png" alt="Selo Reclame Aqui" width={80} height={42} />
                      </Link>
                    </Stack>
                  )}
                </Flex>
                {!isMobile && (
                  <Stack w={{ base: '100px', lg: '150px' }} borderRadius={'8px'} overflow={'hidden'}>
                    <Link href={'https://www.reclameaqui.com.br/empresa/medleve/'} target="_blank">
                      <Image src="/landings/selo-reclame-aqui-1.png" alt="Selo Reclame Aqui" width={120} height={63} />
                    </Link>
                  </Stack>
                )}
              </Flex>
            )}
          </Flex>
        </LandingContainer>
      </Box>
      <Modal isOpen={isOpenDrJoao} onClose={onCloseDrJoao} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={colors.modalBg} borderRadius="16px" pb={4} mx={4}>
          <ModalHeader>
            <H5>Dr. João Brunhara</H5>
            <ModalCloseButton zIndex={1} />
          </ModalHeader>
          <ModalBody>
            <video
              src="https://storage.googleapis.com/videos-omens/Joao_Medleve_0907.mp4"
              controls
              autoPlay
              style={{ width: '100%', borderRadius: '16px' }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isOpenDrCarlos} onClose={onCloseDrCarlos} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={colors.modalBg} borderRadius="16px" pb={4} mx={4}>
          <ModalHeader>
            <H5>Dr. Carlos Seraphim</H5>
            <ModalCloseButton zIndex={1} />
          </ModalHeader>
          <ModalBody>
            <video
              src="https://storage.googleapis.com/videos-omens/Carlos_Medleve_0907.2.mp4"
              controls
              autoPlay
              style={{ width: '100%', borderRadius: '16px' }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default MedleveAboutSection
